import { makeStyles, shorthands, tokens, Button } from '@fluentui/react-components'
import React, { useContext, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { RiskAnalysis, Threat } from '../../../../../api/schemas/schema'
import { DataContext } from '../../../../../context/DataContext'
import { isSystemAdmin } from '../../../../../helpers/permissionHelper'
import { updateThreat, addThreat, deleteThreat } from '../../../../../helpers/riskAnalysisHelper'
import { IItemState } from '../../../../../interfaces/IItemState'
import { ControlledTextInput } from '../../../../common/inputs/TextInput'
import { DraggableModal } from '../../../../common/modal/DraggableModal'
import { ModalActions } from '../../../../common/modal/Modal'
import { MoreOptionsMenuButton } from '../../../../common/other/MoreOptionsMenuButton'
import { Form } from '../../../../common/form/Form'
import { t } from '../../../../../i18n/i18n'

const useStyles = makeStyles({
   form: {
      display: 'flex',
      flexDirection: 'column',
      ...shorthands.gap(tokens.spacingHorizontalXL),
   },
})

type ModalNewThreatProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
   setOpen: React.Dispatch<React.SetStateAction<boolean>>
   open: boolean
   selectedItem: Threat
   disabled?: boolean
}

export const ModalThreat = ({ setRiskAnalysisState, analysisState, open, setOpen, selectedItem, disabled }: ModalNewThreatProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const analysis = analysisState.item
   const {
      control,
      handleSubmit,
      reset,
      formState: { isDirty, isSubmitting },
   } = useForm<Threat>({ disabled: disabled })

   const onSubmit = async (data: Threat) => {
      if (data.id) {
         await updateThreat(setRiskAnalysisState, close, dataContext, data, analysis)
         reset(data)
      } else {
         await addThreat(setRiskAnalysisState, close, dataContext, data, analysis)
      }
   }
   const close = () => {
      reset()
      setOpen(false)
   }

   useEffect(() => {
      if (selectedItem) {
         reset(selectedItem)
      } else {
         reset({
            name: null,
            description: null,
            underlyingMotivation: null,
         })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem])

   const deleteOptions = () => {
      const threatInUse = analysis.consequenceAndProbabilityRisks?.some((c) => c.threatId === selectedItem.id)
      const userHasPermission = isSystemAdmin(userAccount) || analysis.analysisOwner?.id === userAccount?.id

      const deleteDisabled = !userHasPermission || threatInUse
      const deleteTooltipMessage = deleteDisabled
         ? !userHasPermission
            ? t('OnlySystemAdministratorOrRiskOwnerCanDeleteItem', { Item: t('Threat').toLowerCase() })
            : t('ItemIsConnectedToItemAndCantBeDeleted', { ItemA: t('TheThreat'), Article: t('A').toLowerCase(), ItemB: t('Risk').toLowerCase() })
         : ''

      return (
         <MoreOptionsMenuButton
            item={selectedItem}
            deleteFunction={deleteThreat}
            setState={setRiskAnalysisState}
            closeOnDelete={close}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={t('DeleteItem', { Item: t('Threat').toLowerCase() })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('TheThreat').toLowerCase() })}
         />
      )
   }

   return (
      <DraggableModal title={selectedItem ? t('EditThreat') : t('NewThreat')} open={open} onClose={close} secondaryButton={selectedItem ? deleteOptions : null}>
         <Form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
            <ControlledTextInput control={control} name={'name'} label={t('SpecifyCurrentThreat')} required />
            <ControlledTextInput control={control} name={'description'} label={t('Description')} multiline required />
            <ControlledTextInput control={control} name={'underlyingMotivation'} label={t('UnderlyingMotivationAndStand')} multiline />
            <ModalActions>
               <Button onClick={close} disabled={isSubmitting}>
                  {disabled ? t('Close') : t('Cancel')}
               </Button>
               {!disabled && (
                  <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                     {t(selectedItem ? 'Update' : 'Save')}
                  </Button>
               )}
            </ModalActions>
         </Form>
      </DraggableModal>
   )
}
